
export type TranformFn = (str: string) => string;

export function transformParamKeys(params: { [key: string]: any }, tranformFn: TranformFn) {
  if (!params || (params instanceof FormData)) { return params; }
  const transformedParams: { [key: string]: any } = {};
  Object.keys(params).forEach((key: string) => {
    const tranformedKey = tranformFn(key);
    if (typeof params[key] === 'object' && !(params[key] instanceof Array)) {
      transformedParams[tranformedKey] = transformParamKeys(params[key], tranformFn);
    } else if (Array.isArray(params[key])) {
      transformedParams[tranformedKey] = [];
      params[key].forEach((p: any) => {
        if (typeof p === 'object') {
          transformedParams[tranformedKey].push(transformParamKeys(p, tranformFn));
        } else {
          transformedParams[tranformedKey].push(p);
        }
      });
    } else {
      transformedParams[tranformedKey] = params[key];
    }
  });
  return transformedParams;
}

export function camelToSnakeCase(str: string) {
  if (!str) { return str; }
  const snakeCase = str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  // Trim any leading '_'
  return snakeCase.replace(/^_/g, '');
}

export function camelToSnakeCaseParams(params: { [key: string]: any }) {
  return transformParamKeys(params, camelToSnakeCase);
}

export function snakeToCamelCase(str: string) {
  if (!str) { return str; }
  const camelCase = str.replace(/([_][a-z])/g, group => group.toUpperCase().replace('_', ''));
  // Trim any trailing '_'
  return camelCase.replace(/_$/g, '');
}

export function snakeToCamelCaseParams(params: { [key: string]: any }) {
  return transformParamKeys(params, snakeToCamelCase);
};

// NOTE: Libs like https://www.npmjs.com/package/qs provide more robust
// solutions but seem overkill unless serialization becomes more complex.
export function toQueryString(params: { [key: string]: any | any[] }) {
  if (!params) { return ''; }

  const queryStrings: string[] = [];
  for (const [key, value] of Object.entries(camelToSnakeCaseParams(params))) {
    // Abstract equality comparison will catch all null or undefined values
    if (value == null) {
      continue;
    } else if (typeof value === 'string' && !value.length) {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        queryStrings.push(`${key}=${value.join(',')}`);
      }
    } else {
      queryStrings.push(`${key}=${value.toString()}`);
    }
  }

  return queryStrings.join('&');
}

type OmitByFn = (val: any, key: string) => boolean;

export function omitParamsBy(params: { [key: string]: any }, omitFn: OmitByFn) {
  if (!params) { return params; }
  return Object.keys(params)
    .filter(key => !omitFn(params[key], key))
    .reduce((acc: any, key) => { acc[key] = params[key]; return acc; }, {});
}

export const omitFalsyValues = (params: { [key: string]: any }) => {
  if (!params || (params instanceof FormData)) { return params; }
  return omitParamsBy(params, val => !val);
};

export const omitEmptyValues = (params: { [key: string]: any }) => {
  if (!params || (params instanceof FormData)) { return params; }
  return omitParamsBy(params, val => [undefined, null, ''].indexOf(val) > -1);
};
